import React from "react";
import { SettingsLayout } from "@/Layouts/SettingsLayout";
import { route } from "ziggy-js";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/Components/ui/form";
import { Input } from "@/Components/ui/input";
import { Checkbox } from "@/Components/ui/checkbox";
import { Label } from "@/Components/ui/label";

export type AccountProps = {
  user: App.Data.Models.UserData;
};

const formSchema = z.object({
  username: z.string(),
  email: z.string(),
  subscribe_to_announcements: z.boolean(),
  subscribe_to_activity: z.boolean(),
});

const Account = (props: AccountProps) => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: props.user.username,
      email: props.user.email!,
      subscribe_to_announcements: !!props.user.announcements_subscribed_at!,
      subscribe_to_activity: !!props.user.activity_subscribed_at!,
    },
  });

  return (
    <Form {...form} route={route("settings.account.store")}>
      <SettingsLayout
        form={form}
        title="Account"
        description="Change your account information and preferences.">
        <div className="col-span-3 sm:col-span-2">
          <FormField
            control={form.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <Input placeholder="Enter username..." autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="col-span-3 sm:col-span-2">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input placeholder="Enter email..." autoComplete="email" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="col-span-3 space-y-2 sm:col-span-2">
          <Label>Email preferences</Label>
          <FormField
            control={form.control}
            name="subscribe_to_announcements"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Newsletter</FormLabel>
                  <FormDescription>
                    Receive product updates and community announcements.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="subscribe_to_activity"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                <FormControl>
                  <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>New activity</FormLabel>
                  <FormDescription>
                    Receive notifications when there's new activity on rigr.gg.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
      </SettingsLayout>
    </Form>
  );
};

export default Account;
